import { IconUser } from '@tabler/icons-react'
import { useMutation } from '@tanstack/react-query'
import { useRef, useState } from 'react'

import { AstraButton, AstraIcon, AstraSelect } from '~/astra'
import client from '~/clients/api-client'
import optimusClient, { RecurringAssignmentPromptRequest } from '~/clients/optimus-client'
import { handleApiRequest as handleOptimusRequest } from '~/hooks/tanstack-query/optimus/shared/apiWrapper'
import { handleApiRequest } from '~/hooks/tanstack-query/shared/apiWrapper'
import { useUnmount } from '~/hooks/useUnmount'
import { importRecurringAssignments } from '~/store/di-entity.api'
import { selectPractitionerValues } from '~/store/slices/surgeonSchedulerFilterSlice'
import { useStore } from '~/store/store'
import { Practitioner, selectRecurringAssignment, useActivePractitioners } from '~/storm'

import { ChatEntry } from './ChatEntry'
import { ChatEntryList } from './ChatEntryList'
import { ChatInput } from './ChatInput'
import { ChatMessage, LLMSuccessResponse } from './types'

const examplePrompts = ['OP01 hver tirsdag', 'A annenhver fredag partall', 'O OP03 onsdag partall']

type LLMSuccessPreviewMessage = ChatMessage & { response: LLMSuccessResponse }

type RecurringAssignmentCreationProps = {
    onBackClick: () => void
    practitioner?: Practitioner
}
export const RecurringAssignmentCreation = ({ onBackClick, practitioner }: RecurringAssignmentCreationProps) => {
    const [selectedPractitioner, setSelectedPractitioner] = useState<Practitioner | null>(practitioner ?? null)
    const [chatItems, setChatItems] = useState<ChatMessage[]>([])
    const [isTouched, setIsTouched] = useState(false)
    // controlled value due to example prompts
    const [inputValue, setInputValue] = useState('')

    const inputRef = useRef<HTMLTextAreaElement>(null)
    const activePractitioners = useActivePractitioners()
    const filteredPractitioners = useStore(selectPractitionerValues)
    const surgeons = activePractitioners.filter(p => filteredPractitioners.length === 0 || filteredPractitioners.includes(p.short_name))
    const surgeonOptions = surgeons.map(surgeon => ({ label: surgeon.short_name, value: surgeon.id }))

    const hidePractitionerSelection = !!practitioner

    const addChatItem = (item: ChatMessage) => {
        setChatItems(prev => [...prev, item])
    }

    const promptMutation = useMutation({
        mutationFn: async (data: RecurringAssignmentPromptRequest) => {
            const createResponse = await handleOptimusRequest(() => optimusClient.recurringAssignments.createPrompt(data))
            if (createResponse?.type === 'success') {
                await importRecurringAssignments({ uuid: createResponse.recurring_assignment_uuid })
            }
            return createResponse
        },
    })

    const handlePrompt = async (prompt: string) => {
        if (selectedPractitioner === null) {
            return
        }

        addChatItem({ message: prompt, type: 'userMessage', practitioner: selectedPractitioner })

        const res = await promptMutation.mutateAsync({
            prompt: { text: prompt },
            practitioner_uuid: selectedPractitioner.uuid,
        })

        if (res?.type) {
            addChatItem({ type: 'LLMPreview', response: res })
        }
    }

    /* reject remaining assignments that is still in draft during unmount */
    useUnmount(() => {
        const state = useStore.getState()
        const assignmentsForSession = chatItems.filter(
            (item): item is LLMSuccessPreviewMessage => item.type === 'LLMPreview' && item.response.type === 'success'
        )

        assignmentsForSession
            .map(a => selectRecurringAssignment(state, { where: { uuid: a.response.recurring_assignment_uuid } }))
            .filter(a => a?.entity_status === 'draft')
            .forEach(a => {
                const uuid = a?.uuid
                if (uuid) {
                    handleApiRequest({
                        apiCall: () => client.rejectRecurringAssignment(uuid),
                    }).catch(e => {
                        console.warn(`Failed to cleanup assignment ${uuid}: `, e)
                    })
                }
            })
    })

    const isSelectedPractitionerError = isTouched && !selectedPractitioner
    return (
        <div className="flex w-[600px] flex-col">
            <div className="flex flex-col items-center gap-8 p-6">
                <ChatEntryList>
                    {chatItems.map((item, index) => (
                        <ChatEntry key={index} message={item} addMessage={addChatItem} onBackClick={onBackClick} />
                    ))}
                    {promptMutation.isPending ? <ChatEntry onBackClick={onBackClick} addMessage={addChatItem} message={{ type: 'loading' }} /> : null}

                    {promptMutation.isError && (
                        <ChatEntry
                            message={{ type: 'error', message: promptMutation.error.message, originalError: promptMutation.error }}
                            addMessage={addChatItem}
                            onBackClick={onBackClick}
                        />
                    )}
                </ChatEntryList>
                <div className="flex w-full flex-col gap-4 px-6">
                    <ChatInput
                        ref={inputRef}
                        onSendMessage={handlePrompt}
                        onChange={val => {
                            setInputValue(val)
                        }}
                        isValid={() => {
                            setIsTouched(true)
                            return !promptMutation.isPending && !!selectedPractitioner
                        }}
                        placeholder="Skriv regel..."
                        value={inputValue}
                        bottomElement={
                            hidePractitionerSelection ? null : (
                                <AstraSelect
                                    icon={<AstraIcon icon={IconUser} />}
                                    placeholder="Velg kirurg"
                                    options={surgeonOptions}
                                    error={isSelectedPractitionerError}
                                    value={selectedPractitioner?.id}
                                    errorText={isSelectedPractitionerError ? 'Vennligst velg en kirurg for å fortsette' : undefined}
                                    onChange={option => {
                                        setSelectedPractitioner(surgeons.find(s => s.id === option) ?? null)
                                    }}
                                />
                            )
                        }
                    />
                    <div className="flex gap-2">
                        {examplePrompts.map(prompt => (
                            <AstraButton
                                key={prompt}
                                size="small"
                                variant="secondary"
                                label={prompt}
                                onClick={() => {
                                    setInputValue(val => val.concat(prompt))
                                    inputRef.current?.focus()
                                }}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    )
}
